import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Ban, BadgeCheck, Send, Lock, Mail, FilePen, AlertTriangle, Pencil } from 'lucide-react';
import { usePayslip, usePayslips } from '@/composables/payroll/usePayslips';
import { PageContainer } from '@/components/layout/PageContainer';
import { PayslipPdfButton, PAYSLIP_STATUS_STYLES } from './PayslipListPage';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/FormDialog';
import { FormField } from '@/components/forms/FormField';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { formatCurrency, formatDate, formatDateUTC } from '@/lib/formatters';
import { MONTH_OPTIONS, type PayslipItem } from '@/types/payroll';

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm">{value || '—'}</p>
    </div>
  );
}

/**
 * Descriptive fields only — every one of these is a label rather than a figure,
 * which is why they stay editable after publishing. Anything affecting an
 * amount, and the bank details, need a full correction.
 */
const detailsSchema = z.object({
  employeeName: z.string().min(1, 'Name is required'),
  designation: z.string().optional(),
  department: z.string().optional(),
  taxIdentifier: z.string().optional(),
  socialSecurityId: z.string().optional(),
  pensionId: z.string().optional(),
});
type DetailsFormData = z.infer<typeof detailsSchema>;

export function PayslipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { payslip, isLoading } = usePayslip(id ?? '');
  const { publish, markPaid, cancel, emailPayslip, supersede, updateDetails } = usePayslips();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsForm = useForm<DetailsFormData>({ resolver: zodResolver(detailsSchema) });

  if (isLoading) return <LoadingSpinner />;
  if (!payslip) return <EmptyState title="Payslip not found" description="It may have been deleted." />;

  const fmt = (v: string | number) => formatCurrency(v, payslip.currency);
  const lop = Number(payslip.lopDays);
  const items = payslip.items ?? [];
  const section = (type: string) => items.filter((i) => i.type === type);
  const isFrozen = payslip.status !== 'DRAFT';
  // Correcting only makes sense for something already issued. A draft is edited
  // directly; a cancelled or superseded one is history.
  const isIssued = payslip.status === 'PUBLISHED' || payslip.status === 'PAID';
  // Once a run has posted to the ledger its payslips can no longer be replaced:
  // the run wrote one expense for the sum of them, so a correction would leave
  // the document asserting an amount the accounts never recorded. The server
  // refuses it — don't offer a button that always fails.
  const runPosted = payslip.payrollRun?.status === 'APPROVED' || payslip.payrollRun?.status === 'PAID';
  const canCorrect = isIssued && !payslip.supersededBy && !runPosted;
  const period = `${MONTH_OPTIONS[payslip.periodMonth - 1]?.label} ${payslip.periodYear}`;

  return (
    <PageContainer backTo
      title={payslip.payslipNumber}
      description={`${payslip.employeeName} · ${MONTH_OPTIONS[payslip.periodMonth - 1]?.label} ${payslip.periodYear}`}
      actions={
        <>
          <Button variant="outline" onClick={() => navigate('/people/payslips')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back
          </Button>
          <PayslipPdfButton payslip={payslip} />
          {(payslip.status === 'PUBLISHED' || payslip.status === 'PAID') && (
            <Button variant="outline" onClick={() => emailPayslip.mutate(payslip.id)} disabled={emailPayslip.isPending}>
              <Mail className="mr-2 h-4 w-4" />
              {emailPayslip.isPending ? 'Queueing…' : payslip.emailedAt ? 'Re-send email' : 'Email to employee'}
            </Button>
          )}
          {payslip.status === 'DRAFT' && (
            <Button onClick={() => setPublishOpen(true)} disabled={publish.isPending}>
              <Send className="mr-2 h-4 w-4" />{publish.isPending ? 'Publishing…' : 'Publish'}
            </Button>
          )}
          {payslip.status === 'PUBLISHED' && (
            <Button onClick={() => markPaid.mutate(payslip.id)} disabled={markPaid.isPending}>
              <BadgeCheck className="mr-2 h-4 w-4" />Mark as paid
            </Button>
          )}
          {canCorrect && (
            <Button variant="outline" onClick={() => setCorrectOpen(true)} disabled={supersede.isPending}>
              <FilePen className="mr-2 h-4 w-4" />{supersede.isPending ? 'Correcting…' : 'Correct'}
            </Button>
          )}
          {isIssued && (
            <Button variant="outline" onClick={() => setCancelOpen(true)}>
              <Ban className="mr-2 h-4 w-4" />Cancel
            </Button>
          )}
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={`border-0 font-medium ${PAYSLIP_STATUS_STYLES[payslip.status] ?? ''}`}>
          {payslip.status}
        </Badge>
        {isFrozen && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />Figures frozen at issue
          </span>
        )}
        {lop > 0 && (
          <Badge variant="outline" className="border-0 bg-amber-500/15 text-amber-600 dark:text-amber-400">
            {lop} day{lop === 1 ? '' : 's'} loss of pay
          </Badge>
        )}
        {payslip.emailedAt && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Mail className="h-3 w-3" />Emailed to {payslip.emailTo} on {formatDate(payslip.emailedAt)}
          </span>
        )}
        {payslip.emailError && !payslip.emailedAt && (
          <span className="text-xs text-destructive">Email failed: {payslip.emailError}</span>
        )}
      </div>

      {/* A corrected month has two documents. Which one counts must be obvious
          from either of them, not just from the one you happened to open. */}
      {payslip.supersededBy && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-muted bg-muted/40 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">
            This payslip was corrected and is no longer current. It was replaced by
          </span>
          <Link to={`/people/payslips/${payslip.supersededBy.id}`} className="font-medium underline underline-offset-2">
            {payslip.supersededBy.payslipNumber}
          </Link>
          <span className="text-muted-foreground">({fmt(payslip.supersededBy.netPay)} net).</span>
        </div>
      )}
      {payslip.supersedes && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
          <FilePen className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="text-muted-foreground">This is a correction. It supersedes</span>
          <Link to={`/people/payslips/${payslip.supersedes.id}`} className="font-medium underline underline-offset-2">
            {payslip.supersedes.payslipNumber}
          </Link>
          <span className="text-muted-foreground">
            ({fmt(payslip.supersedes.netPay)} net), which stays on record.
          </span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat label="Gross earnings" value={fmt(payslip.grossEarnings)} />
        <Stat label="Deductions" value={`− ${fmt(payslip.totalDeductions)}`} />
        <Stat label="Employer cost" value={fmt(payslip.employerContributions)} />
        <Stat label="Net pay" value={fmt(payslip.netPay)} highlight />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <ItemCard title="Earnings" items={section('EARNING')} total={payslip.grossEarnings} totalLabel="Gross" fmt={fmt} showFull={lop > 0} />
        <ItemCard title="Deductions" items={section('DEDUCTION')} total={payslip.totalDeductions} totalLabel="Total" fmt={fmt} showFull={lop > 0} negative />
        <ItemCard title="Employer contributions" items={section('EMPLOYER_CONTRIBUTION')} total={payslip.employerContributions} totalLabel="Total" fmt={fmt} showFull={lop > 0} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Employee (as issued)</CardTitle>
            {/* These are labels, not figures, so they stay fixable after
                publishing — a misspelled name or a missing PAN does not need a
                whole replacement payslip. */}
            {payslip.status !== 'CANCELLED' && payslip.status !== 'SUPERSEDED' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  detailsForm.reset({
                    employeeName: payslip.employeeName,
                    designation: payslip.designation ?? '',
                    department: payslip.department ?? '',
                    taxIdentifier: payslip.taxIdentifier ?? '',
                    socialSecurityId: payslip.socialSecurityId ?? '',
                    pensionId: payslip.pensionId ?? '',
                  });
                  setDetailsOpen(true);
                }}
              >
                <Pencil className="mr-2 h-3.5 w-3.5" />Fix details
              </Button>
            )}
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" value={payslip.employeeName} />
            <Field label="Employee code" value={payslip.employeeCode} />
            <Field label="Designation" value={payslip.designation} />
            <Field label="Department" value={payslip.department} />
            <Field label="Date of joining" value={formatDateUTC(payslip.dateOfJoining)} />
            {/* Labels come from the payslip, not today's settings — a later rename
                must not retitle a document already handed to the employee. */}
            {payslip.taxIdentifier && <Field label={payslip.taxIdentifierLabel || 'Tax ID'} value={payslip.taxIdentifier} />}
            {payslip.pensionId && <Field label={payslip.pensionIdLabel || 'Pension ID'} value={payslip.pensionId} />}
            {payslip.socialSecurityId && <Field label={payslip.socialSecurityIdLabel || 'Social security'} value={payslip.socialSecurityId} />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Period &amp; payment</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Period" value={`${formatDateUTC(payslip.periodStart)} – ${formatDateUTC(payslip.periodEnd)}`} />
            <Field label="Pay date" value={formatDateUTC(payslip.payDate)} />
            <Field label="Days in period" value={String(payslip.totalDays)} />
            <Field label="Paid days" value={String(Number(payslip.paidDays))} />
            <Field label="Annual CTC" value={fmt(payslip.ctcAnnual)} />
            <Field label="Bank" value={payslip.bankName} />
            <Field label="Account" value={payslip.bankAccountNumber} />
            <Field label="IFSC / routing" value={payslip.bankIfsc} />
            {payslip.publishedAt && <Field label="Published" value={formatDate(payslip.publishedAt)} />}
            {payslip.paidAt && <Field label="Paid" value={formatDate(payslip.paidAt)} />}
            {payslip.cancelledAt && <Field label="Cancelled" value={formatDate(payslip.cancelledAt)} />}
            {payslip.notes && <div className="sm:col-span-2"><Field label="Notes" value={payslip.notes} /></div>}
          </CardContent>
        </Card>
      </div>

      {/* Publishing cannot be undone, and the current UI makes it a single
          click next to Back. Spell out what it locks before it happens. */}
      <ConfirmDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title="Publish this payslip?"
        description={`${payslip.payslipNumber} — ${payslip.employeeName}, ${period}, net ${fmt(payslip.netPay)}.`}
        confirmLabel="Publish"
        onConfirm={() => { publish.mutate(payslip.id); setPublishOpen(false); }}
        isLoading={publish.isPending}
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Every figure is frozen. Attendance, adjustments and amounts can no longer be edited.</li>
          <li>
            {payslip.employee?.userId
              ? 'The employee can see it immediately in My Payslips.'
              : 'It becomes visible to the employee as soon as a login is linked to their record.'}
          </li>
          <li>This cannot be undone. To fix a mistake later you issue a correction, which leaves both documents on record.</li>
        </ul>
      </ConfirmDialog>

      <ConfirmDialog
        open={correctOpen}
        onOpenChange={setCorrectOpen}
        title="Correct this payslip?"
        description={`A replacement for ${period} will be created as a draft, and ${payslip.payslipNumber} will be marked superseded.`}
        confirmLabel="Create correction"
        onConfirm={() => { supersede.mutate({ id: payslip.id }); setCorrectOpen(false); }}
        isLoading={supersede.isPending}
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>The replacement starts as a draft, recomputed from the current salary structure, carrying this payslip’s adjustments.</li>
          <li>Edit the draft as needed, then publish it — nothing reaches the employee until you do.</li>
          <li>{payslip.payslipNumber} stays readable and prints “superseded”; the employee stops seeing it.</li>
        </ul>
      </ConfirmDialog>

      <FormDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        title="Fix details"
        description="Corrects how the payslip describes the employee. No amount changes, and the document keeps its number — for anything affecting a figure, use Correct instead."
        submitLabel="Save"
        isSubmitting={updateDetails.isPending}
        onSubmit={detailsForm.handleSubmit((values) => {
          updateDetails.mutate({ id: payslip.id, data: values });
          setDetailsOpen(false);
        })}
      >
        <div className="space-y-4">
          <FormField form={detailsForm} name="employeeName" label="Name" required />
          <div className="grid grid-cols-2 gap-4">
            <FormField form={detailsForm} name="designation" label="Designation" />
            <FormField form={detailsForm} name="department" label="Department" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField form={detailsForm} name="taxIdentifier" label={payslip.taxIdentifierLabel || 'Tax ID'} />
            <FormField form={detailsForm} name="pensionId" label={payslip.pensionIdLabel || 'Pension ID'} />
          </div>
          <FormField
            form={detailsForm}
            name="socialSecurityId"
            label={payslip.socialSecurityIdLabel || 'Social security'}
            description="Bank details are not editable here — changing where money went needs a full correction."
          />
        </div>
      </FormDialog>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel Payslip"
        description={`${payslip.payslipNumber} will be marked cancelled. It stays on record, and ${period} becomes free again so a new payslip can be issued for it.`}
        variant="destructive"
        confirmLabel="Cancel payslip"
        onConfirm={() => { cancel.mutate(payslip.id); setCancelOpen(false); }}
        isLoading={cancel.isPending}
      />
    </PageContainer>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <Card className={highlight ? 'border-primary/40 bg-primary/5' : undefined}>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function ItemCard({
  title, items, total, totalLabel, fmt, showFull, negative,
}: {
  title: string;
  items: PayslipItem[];
  total: string;
  totalLabel: string;
  fmt: (v: string | number) => string;
  showFull: boolean;
  negative?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {items.map((i) => (
          <div key={i.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate text-muted-foreground">
              {i.label}
              {i.isStatutory && <span className="ml-1 text-[10px] uppercase tracking-wide">stat</span>}
            </span>
            <span className="flex shrink-0 items-baseline gap-2">
              {showFull && i.fullAmount !== i.amount && (
                <span className="text-xs text-muted-foreground line-through">{fmt(i.fullAmount)}</span>
              )}
              <span className="tabular-nums">{negative ? '− ' : ''}{fmt(i.amount)}</span>
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
          <span>{totalLabel}</span>
          <span className="tabular-nums">{negative ? '− ' : ''}{fmt(total)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
